import { Connector, HarnessNode, Wire } from '../types';
import { CONNECTOR, DIMENSIONS } from './constants';
import { connectorHeight } from './connectorLayout';

/** An axis-aligned rectangle in world coordinates. */
export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Normalizes two opposite corners into an axis-aligned rectangle. */
export function boundsFromPoints(
  a: { x: number; y: number },
  b: { x: number; y: number }
): Bounds {
  return {
    minX: Math.min(a.x, b.x),
    minY: Math.min(a.y, b.y),
    maxX: Math.max(a.x, b.x),
    maxY: Math.max(a.y, b.y)
  };
}

/** World-space bounding box of a connector body, sized to its rendered height. */
export function connectorBounds(connector: Connector, height: number = CONNECTOR.HEIGHT): Bounds {
  return {
    minX: connector.xPosition,
    minY: connector.yPosition,
    maxX: connector.xPosition + CONNECTOR.WIDTH,
    maxY: connector.yPosition + height
  };
}

/** World-space bounding box of a harness node, sized to its hit radius. */
export function nodeBounds(node: HarnessNode): Bounds {
  const r = DIMENSIONS.NODE_RADIUS_SELECTED;
  return {
    minX: node.xPosition - r,
    minY: node.yPosition - r,
    maxX: node.xPosition + r,
    maxY: node.yPosition + r
  };
}

/** True when two axis-aligned rectangles overlap (touching edges count). */
export function boundsIntersect(a: Bounds, b: Bounds): boolean {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}

/**
 * Ids of the connectors and nodes the marquee rectangle touches. Any element the
 * box intersects is included (touch semantics), so a partial overlap selects it.
 */
export function elementsInMarquee(
  marquee: Bounds,
  connectors: Connector[],
  nodes: HarnessNode[],
  wires: Wire[] = []
): string[] {
  const ids: string[] = [];
  connectors.forEach(connector => {
    if (boundsIntersect(marquee, connectorBounds(connector, connectorHeight(connector, wires)))) {
      ids.push(connector.id);
    }
  });
  nodes.forEach(node => {
    if (boundsIntersect(marquee, nodeBounds(node))) {
      ids.push(node.id);
    }
  });
  return ids;
}
